import React, { useContext, useEffect, useState } from "react";
import {
  Wallet as WalletIcon,
  ArrowUpRight,
  Upload,
  Building2,
  Smartphone,
  CircleDollarSign,
  CheckCircle,
  XCircle,
  Clock3,
  X,
  CreditCard,
  Copy,
  Eye,
} from "lucide-react";

import AppContext from "../../../context/AppContext";
import axios from "axios";
import Swal from "sweetalert2";

import "../../../assets/css/seller/WalletInSeller/walletInSeller.css";
import SellerMasterLayout from "../../../layout/seller/SellerMasterLayout";

const Wallet = () => {
  const { baseUrl, baseUrlForFiles, loggedInSeller } = useContext(AppContext);

  // ==============================
  // STATES
  // ==============================

  const [showTopupModal, setShowTopupModal] = useState(false);
  const [topupStep, setTopupStep] = useState(1);

  const [topupMethod, setTopupMethod] = useState("");
  const [bankOrWallet, setBankOrWallet] = useState("");
  const [amount, setAmount] = useState("");

  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState("");

  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);

  const [topupRequests, setTopupRequests] = useState([]);

  const [walletStats, setWalletStats] = useState({
    balance: 0,
  });

  // ✅ NEW — transaction detail view modal + proof modal
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedTopup, setSelectedTopup] = useState(null);
  const [showProofModal, setShowProofModal] = useState(false);

  // ==============================
  // HARD-CODED BANK DETAILS
  // Replace these values with your
  // actual Time4Pulse bank details.
  // ==============================

  const bankDetails = [
    {
      id: 1,
      name: "Meezan Bank",
      accountTitle: "Ali Ahmad",
      accountNumber: "0994000786543",
    },
    {
      id: 2,
      name: "UBL - United Bank Limited",
      accountTitle: "Zain Ali",
      accountNumber: "09432679543",
    },
    {
      id: 3,
      name: "HBL - Habib Bank Limited",
      accountTitle: "Ahmad Ajmad",
      accountNumber: "01009876544",
    },
  ];

  // ==============================
  // HARD-CODED MOBILE WALLET DETAILS
  // Replace these values with your
  // actual Time4Pulse wallet details.
  // ==============================

  const mobileWalletDetails = [
    {
      id: 1,
      name: "JazzCash",
      accountTitle: "Pakeeza Naz",
      accountNumber: "03204423069",
    },
    {
      id: 2,
      name: "Easypaisa",
      accountTitle: "Pakeeza Naz",
      accountNumber: "03204423069",
    },
  ];

  // ==============================
  // FETCH TOPUP HISTORY
  // ==============================

  const fetchSellerTopups = () => {
    if (!loggedInSeller?.id) {
      return;
    }

    setHistoryLoading(true);

    axios
      .post(`${baseUrl}/seller/wallet/viewSellerTopup`, {
        tpshop_id: loggedInSeller.id,
      })
      .then((response) => {
        console.log("Seller Topups:", response.data);

        if (response.data.success) {
          setTopupRequests(response.data.topups || []);
        } else {
          setTopupRequests([]);
        }
      })
      .catch((error) => {
        console.log("Topup History Error:", error.response?.data || error);

        setTopupRequests([]);
      })
      .finally(() => {
        setHistoryLoading(false);
      });
  };
  // ==============================
  // FETCH AVAILABLE BALANCE
  // ==============================

  const fetchSellerBalance = () => {
    if (!loggedInSeller?.id) {
      return;
    }

    axios
      .post(`${baseUrl}/seller/wallet/viewSellerBalance`, {
        tpshop_id: loggedInSeller.id,
      })
      .then((response) => {
        console.log("Seller Balance:", response.data);

        if (response.data.success) {
          setWalletStats({
            balance: Number(response.data.balance) || 0,
          });
        }
      })
      .catch((error) => {
        console.log("Balance Error:", error.response?.data || error);
      });
  };

  // ==============================
  // LOAD DATA
  // ==============================

  useEffect(() => {
    if (loggedInSeller?.id) {
      fetchSellerTopups();
      fetchSellerBalance();
    }
  }, [loggedInSeller?.id]);

  // Use the seller wallet balance supplied by the logged-in seller data.
  // The backend should update this value when a top-up is approved
  // and when a completed order applies the commission.

  // ==============================
  // OPEN TOPUP MODAL
  // ==============================

  const openTopupModal = () => {
    setTopupStep(1);
    setTopupMethod("");
    setBankOrWallet("");
    setAmount("");
    setImage(null);
    setImagePreview("");

    setShowTopupModal(true);
  };

  // ==============================
  // CLOSE TOPUP MODAL
  // ==============================

  const closeTopupModal = () => {
    if (loading) {
      return;
    }

    setShowTopupModal(false);
    setTopupStep(1);
    setTopupMethod("");
    setBankOrWallet("");
    setAmount("");
    setImage(null);
    setImagePreview("");
  };

  // ==============================
  // ✅ NEW — OPEN / CLOSE TRANSACTION DETAIL MODAL
  // ==============================

  const openDetailModal = (topup) => {
    setSelectedTopup(topup);
    setShowDetailModal(true);
  };

  const closeDetailModal = () => {
    setShowDetailModal(false);
    setSelectedTopup(null);
  };

  // ==============================
  // SELECT PAYMENT METHOD
  // ==============================

  const selectPaymentMethod = (methodName) => {
    setTopupMethod(methodName);
    setBankOrWallet("");
    setTopupStep(2);
  };

  // ==============================
  // SELECT BANK / MOBILE WALLET
  // ==============================

  const selectBankOrWallet = (name) => {
    setBankOrWallet(name);
  };

  // ==============================
  // GET SELECTED PAYMENT DETAILS
  // ==============================

  const selectedPaymentDetails =
    topupMethod === "Bank"
      ? bankDetails.find((bank) => bank.name === bankOrWallet)
      : mobileWalletDetails.find((wallet) => wallet.name === bankOrWallet);

  // ==============================
  // COPY ACCOUNT NUMBER
  // ==============================

  const copyAccountNumber = async () => {
    if (!selectedPaymentDetails?.accountNumber) {
      return;
    }

    try {
      await navigator.clipboard.writeText(selectedPaymentDetails.accountNumber);

      Swal.fire({
        icon: "success",
        title: "Copied",
        text: "Account number copied successfully.",
        timer: 1200,
        showConfirmButton: false,
      });
    } catch (error) {
      console.log("Copy Error:", error);
    }
  };

  // ==============================
  // IMAGE SELECT
  // ==============================

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) {
      return;
    }

    if (!file.type.startsWith("image/")) {
      Swal.fire({
        icon: "error",
        title: "Invalid File",
        text: "Please select an image file.",
      });

      e.target.value = "";
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      Swal.fire({
        icon: "error",
        title: "File Too Large",
        text: "Image size must be less than 2MB.",
      });

      e.target.value = "";
      return;
    }

    setImage(file);

    const reader = new FileReader();

    reader.onloadend = () => {
      setImagePreview(reader.result);
    };

    reader.readAsDataURL(file);
  };

  // ==============================
  // REMOVE IMAGE
  // ==============================

  const removeImage = () => {
    setImage(null);
    setImagePreview("");

    const fileInput = document.getElementById("topup-proof");

    if (fileInput) {
      fileInput.value = "";
    }
  };

  // ==============================
  // VALIDATE TOPUP
  // ==============================

  const validateTopup = () => {
    if (!topupMethod) {
      Swal.fire({
        icon: "warning",
        title: "Payment Method Required",
        text: "Please select a payment method.",
      });

      return false;
    }

    if (!bankOrWallet) {
      Swal.fire({
        icon: "warning",
        title: topupMethod === "Bank" ? "Select Bank" : "Select Mobile Wallet",
        text:
          topupMethod === "Bank"
            ? "Please select your bank."
            : "Please select your mobile wallet.",
      });

      return false;
    }

    if (!amount || Number(amount) <= 0) {
      Swal.fire({
        icon: "warning",
        title: "Enter Amount",
        text: "Please enter a valid top up amount.",
      });

      return false;
    }

    if (!image) {
      Swal.fire({
        icon: "warning",
        title: "Payment Proof Required",
        text: "Please upload your payment proof.",
      });

      return false;
    }

    return true;
  };

  // ==============================
  // SUBMIT TOPUP
  // ==============================

  const handleTopupSubmit = (e) => {
    e.preventDefault();

    if (!loggedInSeller?.id) {
      Swal.fire({
        icon: "error",
        title: "Seller Not Found",
        text: "Please login again.",
      });

      return;
    }

    if (!validateTopup()) {
      return;
    }

    const formData = new FormData();

    formData.append("tpshop_id", loggedInSeller.id);
    formData.append("amount", amount);
    formData.append("method", topupMethod);
    formData.append("bank_or_wallet", bankOrWallet);
    formData.append("image", image);

    setLoading(true);

    axios
      .post(`${baseUrl}/seller/wallet/addtopup`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then((response) => {
        console.log("Topup Response:", response.data);

        if (response.data.success) {
          closeTopupModal();

          Swal.fire({
            icon: "success",
            title: "Top Up Submitted",
            text:
              response.data.message ||
              "Your top up request has been submitted successfully.",
            confirmButtonColor: "#7c3aed",
          });

          fetchSellerTopups();
        } else {
          Swal.fire({
            icon: "error",
            title: "Request Failed",
            text: response.data.message || "Unable to submit top up request.",
          });
        }
      })
      .catch((error) => {
        console.log("Topup Submit Error:", error.response?.data || error);

        let message = "Something went wrong.";

        if (error.response?.data?.message) {
          message = error.response.data.message;
        }

        if (error.response?.data?.errors) {
          const errors = error.response.data.errors;
          const firstError = Object.values(errors)?.[0]?.[0];

          if (firstError) {
            message = firstError;
          }
        }

        Swal.fire({
          icon: "error",
          title: "Top Up Failed",
          text: message,
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // ==============================
  // FORMAT AMOUNT
  // ==============================

  const formatAmount = (value) => {
    const number = Number(value || 0);

    return number.toLocaleString("en-PK");
  };

  // ==============================
  // FORMAT DATE
  // ==============================

  const formatDate = (date) => {
    if (!date) {
      return "—";
    }

    const parsedDate = new Date(date);

    if (Number.isNaN(parsedDate.getTime())) {
      return "—";
    }

    return parsedDate.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  // ==============================
  // PAYMENT PROOF URL
  // ==============================

  const getFileUrl = (file) => {
    if (!file) {
      return "";
    }

    if (String(file).startsWith("http")) {
      return file;
    }

    return `${baseUrlForFiles}${String(file).startsWith("/") ? "" : "/"}${file}`;
  };

  // ==============================
  // STATUS BADGE
  // ==============================

  const StatusBadge = ({ status }) => {
    const normalizedStatus = String(status || "")
      .toLowerCase()
      .trim();

    if (
      normalizedStatus === "approved" ||
      normalizedStatus === "accepted" ||
      normalizedStatus === "complete" ||
      normalizedStatus === "completed"
    ) {
      return (
        <span className="wlt-status wlt-status--approved">
          <CheckCircle size={13} />
          Approved
        </span>
      );
    }

    if (normalizedStatus === "rejected" || normalizedStatus === "declined") {
      return (
        <span className="wlt-status wlt-status--rejected">
          <XCircle size={13} />
          Rejected
        </span>
      );
    }

    return (
      <span className="wlt-status wlt-status--pending">
        <Clock3 size={13} />
        Pending
      </span>
    );
  };

  // ==============================
  // RENDER
  // ==============================

  return (
    <SellerMasterLayout>
      <div className="wlt-page">
        <div className="wlt-container">
          {/* PAGE HEADER */}
          <div className="wlt-page-head">
            <div>
              <h1 className="wlt-heading">Wallet</h1>
              <p className="wlt-subheading">
                Manage your wallet and top up your balance
              </p>
            </div>
          </div>

          {/* =========================
              AVAILABLE BALANCE
          ========================== */}

          <div
            className="wlt-stats-row"
            style={{ width: "100%", display: "block" }}
          >
            <div
              className="wlt-stat-card"
              style={{ width: "100%", minWidth: "100%" }}
            >
              <div className="wlt-stat-icon wlt-stat-icon--balance">
                <WalletIcon size={20} />
              </div>

              <div>
                <p className="wlt-stat-label">Available Balance</p>

                <h2 className="wlt-stat-value">
                  PKR {formatAmount(walletStats.balance)}
                </h2>
              </div>
            </div>
          </div>
          {/* =========================
              TRANSACTION HISTORY
          ========================== */}

          <div className="wlt-history-card">
            <div className="wlt-history-head">
              <div>
                <h2>Transaction History</h2>
              </div>

              <button
                type="button"
                className="wlt-topup-btn"
                onClick={openTopupModal}
              >
                <ArrowUpRight size={15} />
                Top Up
              </button>
            </div>

            <div className="wlt-history-body">
              {historyLoading ? (
                <div className="wlt-empty-state">
                  <div className="wlt-loading-spinner"></div>
                  <p>Loading transactions...</p>
                </div>
              ) : topupRequests.length === 0 ? (
                <div className="wlt-empty-state">
                  <div className="wlt-empty-icon">
                    <WalletIcon size={25} />
                  </div>

                  <h3>No transactions found</h3>

                  <p>Your top up transactions will appear here.</p>
                </div>
              ) : (
                <div className="wlt-transaction-list">
                  {topupRequests.map((topup, index) => (
                    <div
                      className="wlt-transaction-row"
                      key={topup.id || index}
                    >
                      <div className="wlt-transaction-left">
                        <div className="wlt-transaction-icon">
                          <ArrowUpRight size={17} />
                        </div>

                        <div className="wlt-transaction-info">
                          <h3>Top Up</h3>

                          <p>{formatDate(topup.created_at)}</p>

                          {/* ✅ UPDATED — ab bank/wallet ka naam saaf tarah dikh raha hai */}
                          <small>
                            {topup.method || "Payment"}
                            {topup.bank_or_wallet
                              ? ` • ${topup.bank_or_wallet}`
                              : ""}
                          </small>
                        </div>
                      </div>

                      <div className="wlt-transaction-right">
                        <strong>+ PKR {formatAmount(topup.amount)}</strong>

                        <StatusBadge status={topup.status} />

                        {/* ==========================================
                            ✅ NEW — VIEW BUTTON
                            Popup ke andar hi bank/account/proof
                            sab kuch dikhata hai, bahar navigate
                            nahi karta.
                        ========================================== */}
                        <button
                          type="button"
                          className="wlt-view-btn"
                          onClick={() => openDetailModal(topup)}
                          style={{
                            marginTop: "6px",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "4px",
                            border: "1px solid #e5e7eb",
                            background: "#fff",
                            borderRadius: "6px",
                            padding: "4px 10px",
                            fontSize: "12px",
                            cursor: "pointer",
                          }}
                        >
                          <Eye size={13} />
                          View
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ==============================
          ✅ NEW — TRANSACTION DETAIL MODAL
          (Bank name, account holder/number agar match mil jaye,
          amount, status, aur payment proof — sab isi popup ke andar)
      =============================== */}

      {showDetailModal && selectedTopup && (
        <div
          className="wlt-modal-overlay"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              closeDetailModal();
            }
          }}
        >
          <div className="wlt-modal" style={{ maxWidth: "480px" }}>
            <div className="wlt-modal-header">
              <div className="wlt-modal-title-wrap">
                <div className="wlt-modal-title-icon">
                  <WalletIcon size={19} />
                </div>

                <div>
                  <h2>Top Up Details</h2>
                  <p>Transaction #{selectedTopup.id}</p>
                </div>
              </div>

              <button
                type="button"
                className="wlt-modal-close"
                onClick={closeDetailModal}
              >
                <X size={19} />
              </button>
            </div>

            <div className="wlt-modal-body">
              <div style={{ display: "grid", gap: "12px" }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <span style={{ color: "#777", fontSize: "13px" }}>
                    Amount
                  </span>
                  <strong>
                    PKR {formatAmount(selectedTopup.amount)}
                  </strong>
                </div>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <span style={{ color: "#777", fontSize: "13px" }}>
                    Method
                  </span>
                  <strong>{selectedTopup.method || "—"}</strong>
                </div>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <span style={{ color: "#777", fontSize: "13px" }}>
                    Bank / Wallet
                  </span>
                  <strong>{selectedTopup.bank_or_wallet || "—"}</strong>
                </div>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <span style={{ color: "#777", fontSize: "13px" }}>
                    Date
                  </span>
                  <strong>{formatDate(selectedTopup.created_at)}</strong>
                </div>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span style={{ color: "#777", fontSize: "13px" }}>
                    Status
                  </span>
                  <StatusBadge status={selectedTopup.status} />
                </div>
              </div>

              {/* Payment Proof box */}
              <div
                style={{
                  marginTop: "16px",
                  padding: "12px",
                  borderRadius: "10px",
                  background: "#f8f9fa",
                  border: "1px solid #e5e7eb",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div>
                  <strong style={{ fontSize: "13px" }}>Payment Proof</strong>
                  <div className="text-muted" style={{ fontSize: "12px" }}>
                    {selectedTopup.image ? "Uploaded" : "Not available"}
                  </div>
                </div>

                {selectedTopup.image && (
                  // ==========================================
                  // ✅ FIX — pehle ye <a target="_blank"> tha
                  // jo popup se bahar naya tab khol deta tha.
                  // Ab button popup ke andar hi proof image
                  // ek doosri modal me dikhata hai.
                  // ==========================================
                  <button
                    type="button"
                    onClick={() => setShowProofModal(true)}
                    style={{
                      border: "1px solid #7c3aed",
                      color: "#7c3aed",
                      background: "transparent",
                      borderRadius: "6px",
                      padding: "6px 12px",
                      fontSize: "12px",
                      cursor: "pointer",
                    }}
                  >
                    View Proof
                  </button>
                )}
              </div>
            </div>

            <div className="wlt-modal-footer">
              <button
                type="button"
                className="wlt-cancel-btn"
                onClick={closeDetailModal}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ==============================
          ✅ NEW — PAYMENT PROOF IMAGE MODAL
          (Proof popup ke andar hi khulti hai, bahar navigate nahi karti)
      =============================== */}

      {showProofModal && selectedTopup && (
        <div
          className="wlt-modal-overlay"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              setShowProofModal(false);
            }
          }}
        >
          <div className="wlt-modal" style={{ maxWidth: "560px" }}>
            <div className="wlt-modal-header">
              <div className="wlt-modal-title-wrap">
                <div>
                  <h2>Payment Proof</h2>
                </div>
              </div>

              <button
                type="button"
                className="wlt-modal-close"
                onClick={() => setShowProofModal(false)}
              >
                <X size={19} />
              </button>
            </div>

            <div
              className="wlt-modal-body"
              style={{ textAlign: "center" }}
            >
              {selectedTopup.image ? (
                <img
                  src={getFileUrl(selectedTopup.image)}
                  alt="Payment Proof"
                  style={{
                    maxWidth: "100%",
                    maxHeight: "500px",
                    borderRadius: "8px",
                    objectFit: "contain",
                  }}
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
              ) : (
                <p>No image found</p>
              )}
            </div>

            <div className="wlt-modal-footer">
              <button
                type="button"
                className="wlt-cancel-btn"
                onClick={() => setShowProofModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ==============================
          TOP UP MODAL
      =============================== */}

      {showTopupModal && (
        <div
          className="wlt-modal-overlay"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              closeTopupModal();
            }
          }}
        >
          <div className="wlt-modal">
            {/* MODAL HEADER */}

            <div className="wlt-modal-header">
              <div className="wlt-modal-title-wrap">
                <div className="wlt-modal-title-icon">
                  <WalletIcon size={19} />
                </div>

                <div>
                  <h2>Top Up Wallet</h2>

                  <p>
                    Add money to your wallet and keep your business running.
                  </p>
                </div>
              </div>

              <button
                type="button"
                className="wlt-modal-close"
                onClick={closeTopupModal}
                disabled={loading}
              >
                <X size={19} />
              </button>
            </div>

            {/* MODAL BODY */}

            <form onSubmit={handleTopupSubmit}>
              <div className="wlt-modal-body">
                {/* STEP 1 */}

                <div className="wlt-step-heading">
                  <span className="wlt-step-number">1</span>

                  <span>Select Payment Method</span>
                </div>

                <div className="wlt-payment-methods">
                  {/* BANK */}

                  <button
                    type="button"
                    className={`wlt-payment-card ${
                      topupMethod === "Bank" ? "wlt-payment-card--active" : ""
                    }`}
                    onClick={() => selectPaymentMethod("Bank")}
                  >
                    <div className="wlt-payment-icon">
                      <Building2 size={23} />
                    </div>

                    <div>
                      <strong>Bank Account</strong>

                      <span>Transfer from your bank account</span>
                    </div>
                  </button>

                  {/* MOBILE */}

                  <button
                    type="button"
                    className={`wlt-payment-card ${
                      topupMethod === "Mobile" ? "wlt-payment-card--active" : ""
                    }`}
                    onClick={() => selectPaymentMethod("Mobile")}
                  >
                    <div className="wlt-payment-icon">
                      <Smartphone size={23} />
                    </div>

                    <div>
                      <strong>Mobile Wallet</strong>

                      <span>JazzCash or Easypaisa</span>
                    </div>
                  </button>
                </div>

                {/* STEP 2 */}

                {topupMethod && (
                  <>
                    <div className="wlt-step-divider"></div>

                    <div className="wlt-step-heading">
                      <span className="wlt-step-number">2</span>
                    </div>

                    <p
                      style={{
                        margin: "0 0 12px",
                        fontSize: "13px",
                        color: "#666",
                        lineHeight: 1.5,
                      }}
                    >
                      <strong>Note:</strong> Please send the top-up money into
                      the above account details and upload the payment proof
                      below.
                    </p>

                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns:
                          topupMethod === "Bank"
                            ? "repeat(3, minmax(0, 1fr))"
                            : "repeat(2, minmax(0, 1fr))",
                        gap: "10px",
                      }}
                    >
                      {(topupMethod === "Bank"
                        ? bankDetails
                        : mobileWalletDetails
                      ).map((item) => (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() => selectBankOrWallet(item.name)}
                          style={{
                            textAlign: "left",
                            padding: "13px",
                            borderRadius: "10px",
                            border:
                              bankOrWallet === item.name
                                ? "2px solid #7c3aed"
                                : "1px solid #e5e7eb",
                            background:
                              bankOrWallet === item.name ? "#faf5ff" : "#fff",
                            cursor: "pointer",
                          }}
                        >
                          <strong
                            style={{
                              display: "block",
                              fontSize: "14px",
                              marginBottom: "5px",
                            }}
                          >
                            {item.name}
                          </strong>
                          <span
                            style={{
                              display: "block",
                              fontSize: "12px",
                              color: "#777",
                            }}
                          >
                            Click to view account details
                          </span>
                        </button>
                      ))}
                    </div>

                    {/* =========================
                        SELECTED ACCOUNT DETAILS
                    ========================== */}

                    {selectedPaymentDetails && (
                      <div
                        style={{
                          marginTop: "14px",
                          padding: "16px",
                          borderRadius: "12px",
                          background: "#f8f9fa",
                          border: "1px solid #e5e7eb",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginBottom: "12px",
                          }}
                        >
                          <strong
                            style={{
                              fontSize: "16px",
                            }}
                          >
                            {selectedPaymentDetails.name}
                          </strong>
                        </div>

                        <div
                          style={{
                            display: "grid",
                            gap: "9px",
                          }}
                        >
                          <div>
                            <span
                              style={{
                                display: "block",
                                fontSize: "12px",
                                color: "#777",
                              }}
                            >
                              Account Holder Name
                            </span>

                            <strong>
                              {selectedPaymentDetails.accountTitle}
                            </strong>
                          </div>

                          <div>
                            <span
                              style={{
                                display: "block",
                                fontSize: "12px",
                                color: "#777",
                              }}
                            >
                              Account Number
                            </span>

                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                              }}
                            >
                              <strong>
                                {selectedPaymentDetails.accountNumber}
                              </strong>

                              <button
                                type="button"
                                onClick={copyAccountNumber}
                                style={{
                                  border: "none",
                                  background: "transparent",
                                  cursor: "pointer",
                                  padding: "2px",
                                }}
                                title="Copy account number"
                              >
                                <Copy size={15} />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* =========================
                        AMOUNT
                    ========================== */}

                    <div
                      className="wlt-form-group"
                      style={{ marginTop: "18px" }}
                    >
                      <label>Amount to Top Up</label>

                      <div className="wlt-input-wrap">
                        <CircleDollarSign size={17} />

                        <input
                          type="number"
                          min="1"
                          value={amount}
                          onChange={(e) => setAmount(e.target.value)}
                          placeholder="Enter amount"
                          className="wlt-input wlt-input-with-icon"
                        />
                      </div>

                      <p className="wlt-balance-hint">
                        Available Balance:
                        <strong>
                          {" "}
                          PKR {formatAmount(walletStats.balance)}
                        </strong>
                      </p>
                    </div>

                    {/* =========================
                        PAYMENT PROOF
                    ========================== */}

                    <div className="wlt-form-group">
                      <label>Payment Proof</label>

                      {!imagePreview ? (
                        <label htmlFor="topup-proof" className="wlt-upload-box">
                          <Upload size={27} />

                          <strong>Click to upload</strong>

                          <span>PNG, JPG or WEBP (Max 2MB)</span>
                        </label>
                      ) : (
                        <div className="wlt-preview-box">
                          <img src={imagePreview} alt="Payment proof preview" />

                          <div className="wlt-preview-info">
                            <strong>Payment proof selected</strong>

                            <span>{image?.name}</span>

                            <button type="button" onClick={removeImage}>
                              Remove
                            </button>
                          </div>
                        </div>
                      )}

                      <input
                        id="topup-proof"
                        type="file"
                        accept="image/png,image/jpeg,image/jpg,image/webp"
                        onChange={handleImageChange}
                        hidden
                      />
                    </div>
                  </>
                )}
              </div>

              {/* MODAL FOOTER */}

              <div className="wlt-modal-footer">
                <button
                  type="button"
                  className="wlt-cancel-btn"
                  onClick={closeTopupModal}
                  disabled={loading}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="wlt-submit-btn"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="wlt-button-spinner"></span>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <ArrowUpRight size={16} />
                      Top Up
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </SellerMasterLayout>
  );
};

export default Wallet;